/**
 * PipelineResolvePage
 *
 * Allows an operator to resolve a `pending_assets` pipeline job by:
 *  1. Viewing which slots the pipeline already filled.
 *  2. Uploading assets for missing or unmatched required slots.
 *  3. Submitting the updated props to queue the job for rendering.
 *
 * The job's template version is read from the Firestore job document and
 * compared against the current template definition.  A mismatch surfaces a
 * clear error and prevents queuing, preserving the spec §12 hard constraint.
 *
 * Pipeline-sourced text slots are displayed read-only per spec §5/§13.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { AppLayout } from '../layouts/AppLayout';
import { Card, Badge, Button } from '../components/ui';
import { useTemplates, TemplateSlot } from '../hooks/useTemplates';
import { useAssetUpload } from '../hooks/useAssetUpload';
import { useJobStatus } from '../hooks/useJobStatus';
import { GenerationProgress } from '../components/generate/GenerationProgress';
import { UploadArea } from '../components/ui/UploadArea';
import { getAcceptedTypes } from '../lib/slotProps';
import {
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Circle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PipelineJobData {
  id: string;
  templateId: string;
  templateVersion?: string;
  name?: string;
  status: string;
  props: Record<string, unknown>;
  missing_required_slots: string[];
  unmatched_slots: string[];
  runId?: string | null;
  pipelinePath?: string | null;
  outputFormat?: string;
}

/** State tracked for each slot that needs operator attention. */
interface SlotFill {
  slotId: string;
  file: File | null;
  storageUrl: string | null;
  cropAnchor: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isPipelineText(prop: unknown): boolean {
  if (!prop || typeof prop !== 'object') return false;
  return (prop as Record<string, unknown>).source === 'pipeline';
}

function isSlotFilled(prop: unknown): boolean {
  if (prop === null || prop === undefined) return false;
  if (typeof prop !== 'object') return false;
  const p = prop as Record<string, unknown>;
  if (p.type === 'text') return typeof p.value === 'string' && (p.value as string).trim().length > 0;
  return typeof p.uri === 'string' && (p.uri as string).length > 0;
}

function slotFillText(prop: unknown): string | null {
  if (!prop || typeof prop !== 'object') return null;
  const p = prop as Record<string, unknown>;
  return p.type === 'text' ? String(p.value ?? '') : null;
}

function slotFillUri(prop: unknown): string | null {
  if (!prop || typeof prop !== 'object') return null;
  const p = prop as Record<string, unknown>;
  return typeof p.uri === 'string' ? p.uri : null;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SlotRowProps {
  slot: TemplateSlot;
  existingProp: unknown;
  fillState: SlotFill | undefined;
  isMissing: boolean;
  isUnmatched: boolean;
  onFileAdded: (slotId: string, file: File) => void;
  onFileRemoved: (slotId: string) => void;
  disabled: boolean;
}

function SlotRow({
  slot,
  existingProp,
  fillState,
  isMissing,
  isUnmatched,
  onFileAdded,
  onFileRemoved,
  disabled,
}: SlotRowProps) {
  const filled = fillState?.file || fillState?.storageUrl
    ? true
    : isSlotFilled(existingProp);

  const textValue = slotFillText(existingProp);
  const assetUri = slotFillUri(existingProp);
  const isPipelineTextSlot = slot.type === 'text' && isPipelineText(existingProp);

  return (
    <div className="p-4 bg-bg-tertiary border border-border-subtle rounded-md space-y-2">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {filled ? (
            <CheckCircle2 className="w-4 h-4 text-status-online flex-shrink-0" />
          ) : (
            <Circle className="w-4 h-4 text-status-offline flex-shrink-0" />
          )}
          <span className="text-sm font-semibold text-text-primary">{slot.label}</span>
          {slot.required && <Badge size="sm" variant="error">Required</Badge>}
          <Badge size="sm" variant="default">{slot.type}</Badge>
          {isUnmatched && (
            <Badge size="sm" variant="warning">Unmatched</Badge>
          )}
          {isPipelineTextSlot && (
            <Badge size="sm" variant="info">Pipeline</Badge>
          )}
        </div>
      </div>

      {/* Unmatched notice */}
      {isUnmatched && (
        <p className="text-xs text-yellow-600 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          No pipeline asset matched this slot. Please upload manually.
        </p>
      )}

      {/* Text slot — read-only when pipeline-sourced */}
      {slot.type === 'text' && (
        <div className="text-sm">
          {textValue ? (
            <div className="p-2 bg-bg-secondary border border-border-subtle rounded text-text-primary break-words">
              {textValue}
              {isPipelineTextSlot && (
                <span className="ml-2 text-xs text-text-tertiary">(read-only)</span>
              )}
            </div>
          ) : (
            <p className="text-xs text-text-tertiary italic">No text value — slot will pass null to Remotion.</p>
          )}
        </div>
      )}

      {/* Image/video slot — show existing asset URI or upload zone */}
      {(slot.type === 'image' || slot.type === 'video') && (
        <>
          {assetUri && !fillState?.file && !fillState?.storageUrl && (
            <p className="text-xs text-text-secondary break-all">
              <span className="font-medium">Asset: </span>{assetUri}
            </p>
          )}
          {(isMissing || isUnmatched) && (
            <UploadArea
              onFilesAdded={(files) => files[0] && onFileAdded(slot.slotId, files[0])}
              onFileRemove={() => onFileRemoved(slot.slotId)}
              uploadedFiles={
                fillState?.file
                  ? [{ file: fillState.file }]
                  : fillState?.storageUrl
                  ? [{ name: 'Uploaded asset' }]
                  : []
              }
              accept={getAcceptedTypes(slot.type)}
              maxFiles={1}
              disabled={disabled}
              helperText={`Upload ${slot.type} for this slot`}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PipelineResolvePage
// ---------------------------------------------------------------------------

function PipelineResolvePage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { templates } = useTemplates();
  const { uploadMultiple, uploading } = useAssetUpload();
  const { jobStatus } = useJobStatus(jobId ?? null);

  const [job, setJob] = useState<PipelineJobData | null>(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState<string | null>(null);
  const [fillStates, setFillStates] = useState<SlotFill[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Subscribe to the job document in real-time.
  useEffect(() => {
    if (!jobId) return;
    setJobLoading(true);
    const unsub = onSnapshot(
      doc(db, 'jobs', jobId),
      (snap) => {
        if (!snap.exists()) {
          setJobError('Job not found.');
          setJobLoading(false);
          return;
        }
        const d = snap.data();
        const j: PipelineJobData = {
          id: snap.id,
          templateId: d.templateId ?? '',
          templateVersion: d.templateVersion ?? undefined,
          name: d.name ?? undefined,
          status: d.status ?? 'queued',
          props: typeof d.props === 'object' && d.props !== null ? d.props as Record<string, unknown> : {},
          missing_required_slots: Array.isArray(d.missing_required_slots) ? d.missing_required_slots : [],
          unmatched_slots: Array.isArray(d.unmatched_slots) ? d.unmatched_slots : [],
          runId: d.runId ?? null,
          pipelinePath: d.pipelinePath ?? null,
          outputFormat: d.outputFormat ?? 'mp4',
        };
        setJob(j);
        setJobLoading(false);
      },
      (err) => {
        setJobError(err.message);
        setJobLoading(false);
      },
    );
    return () => unsub();
  }, [jobId]);

  // Initialise fillStates once when the job identity (id) and template library
  // are both known.  We intentionally only re-run on id changes — not on live
  // slot updates — so that in-progress operator fills are never silently reset
  // by a Firestore template update.  The template is looked up fresh inside the
  // effect so we don't need to list template.slots as a dep.
  const jobTemplateId = job?.templateId;
  const jobDocId = job?.id;
  useEffect(() => {
    if (!jobDocId || !jobTemplateId || templates.length === 0) return;
    const tmpl = templates.find((t) => t.id === jobTemplateId);
    if (!tmpl || !job) return;
    const needsFilling = [
      ...new Set([...job.missing_required_slots, ...job.unmatched_slots]),
    ];
    setFillStates(
      needsFilling.map((slotId) => ({
        slotId,
        file: null,
        storageUrl: null,
        cropAnchor:
          tmpl.slots.find((s) => s.slotId === slotId)?.specs?.crop_anchor ?? 'center',
      })),
    );
  }, [jobDocId, jobTemplateId, templates]); // re-run only when identity changes

  // Derive template from the templates list (outside the effect for rendering).
  const template = templates.find((t) => t.id === job?.templateId);

  const handleFileAdded = (slotId: string, file: File) => {
    setFillStates((prev) =>
      prev.map((fs) => (fs.slotId === slotId ? { ...fs, file, storageUrl: null } : fs)),
    );
  };

  const handleFileRemoved = (slotId: string) => {
    setFillStates((prev) =>
      prev.map((fs) => (fs.slotId === slotId ? { ...fs, file: null, storageUrl: null } : fs)),
    );
  };

  // ── Version check ──────────────────────────────────────────────────────────
  const templateVersionMismatch =
    !!job?.templateVersion &&
    !!template?.template_version &&
    job.templateVersion !== template.template_version;

  // ── Render-readiness ───────────────────────────────────────────────────────
  // All slots in missing_required_slots must have a file or storageUrl.
  const unresolvedRequired = (job?.missing_required_slots ?? []).filter((slotId) => {
    const fs = fillStates.find((f) => f.slotId === slotId);
    // Also accept if the existing prop already has a uri (operator left it as-is).
    const existingProp = job?.props?.[slotId];
    return !(fs?.file || fs?.storageUrl || isSlotFilled(existingProp));
  });

  const canSubmit =
    !submitting &&
    !uploading &&
    !templateVersionMismatch &&
    unresolvedRequired.length === 0;

  const isRenderingOrDone =
    jobStatus?.status === 'queued' ||
    jobStatus?.status === 'rendering' ||
    jobStatus?.status === 'uploading' ||
    jobStatus?.status === 'preprocessing' ||
    jobStatus?.status === 'completed';

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!job || !jobId || !template) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const projectId = `pipeline_resolve_${Date.now()}`;

      // Upload new files.
      const toUpload = fillStates
        .filter((fs) => fs.file !== null)
        .map((fs) => ({ file: fs.file!, slotId: fs.slotId, uploaded: false }));
      const uploaded = await uploadMultiple(toUpload, projectId);

      // Merge new uploads into the existing props.
      const mergedProps: Record<string, unknown> = { ...job.props };
      for (const fs of fillStates) {
        const uploadResult = uploaded.find((u) => u.slotId === fs.slotId);
        const storageUrl = uploadResult?.storageUrl ?? fs.storageUrl;
        if (!storageUrl) continue;
        const slot = template.slots.find((s) => s.slotId === fs.slotId);
        if (!slot) continue;
        mergedProps[fs.slotId] = {
          type: slot.type,
          uri: storageUrl,
          fit_mode: slot.specs?.fit_mode ?? 'cover',
          crop_anchor: fs.cropAnchor ?? slot.specs?.crop_anchor ?? 'center',
        };
      }

      // Update the Firestore job: merge props, clear pending fields, queue it.
      await updateDoc(doc(db, 'jobs', jobId), {
        props: mergedProps,
        missing_required_slots: [],
        unmatched_slots: [],
        status: 'queued',
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit job');
      setSubmitting(false);
    }
    // Leave submitting=true so UI stays stable while status transitions.
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (jobLoading) {
    return (
      <AppLayout title="Pipeline Job" subtitle="Loading job…">
        <div className="max-w-3xl mx-auto">
          <p className="text-sm text-text-secondary">Loading…</p>
        </div>
      </AppLayout>
    );
  }

  if (jobError || !job) {
    return (
      <AppLayout title="Pipeline Job" subtitle="Job not found">
        <div className="max-w-3xl mx-auto">
          <Card>
            <p className="text-sm text-accent-red">{jobError ?? 'Job not found.'}</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate('/jobs')}>
              Back to Jobs
            </Button>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // Once the job is queued/rendering, show progress.
  if (isRenderingOrDone) {
    return (
      <AppLayout
        title={job.name ?? 'Pipeline Job'}
        subtitle="Job submitted — tracking progress"
      >
        <div className="max-w-3xl mx-auto space-y-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Jobs
          </Button>
          <GenerationProgress
            jobStatus={jobStatus}
          />
        </div>
      </AppLayout>
    );
  }

  if (job.status !== 'pending_assets') {
    return (
      <AppLayout title={job.name ?? 'Pipeline Job'} subtitle={`Status: ${job.status}`}>
        <div className="max-w-3xl mx-auto">
          <Card>
            <p className="text-sm text-text-secondary">
              This job is in <strong>{job.status}</strong> status and does not require operator action.
            </p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => navigate('/jobs')}>
              Back to Jobs
            </Button>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const needsAttention = [
    ...new Set([...(job.missing_required_slots ?? []), ...(job.unmatched_slots ?? [])]),
  ];

  const templateSlots: TemplateSlot[] = template?.slots ?? [];

  return (
    <AppLayout
      title={job.name ?? 'Resolve Pipeline Job'}
      subtitle="Fill missing slots to queue this job for rendering"
    >
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Back button */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Jobs
        </Button>

        {/* Template version mismatch banner */}
        {templateVersionMismatch && (
          <Card className="border-accent-red">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-accent-red flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-accent-red">Template version mismatch</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  This job was created against template version{' '}
                  <strong>{job.templateVersion}</strong>, but the current version is{' '}
                  <strong>{template?.template_version}</strong>. Rendering is blocked.
                  Contact the template owner to resolve the version conflict.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Summary card */}
        <Card>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-semibold text-text-primary">
                {template?.name ?? job.templateId}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">
                Run ID: {job.runId ?? '—'} · Template v{job.templateVersion ?? '?'}
              </p>
            </div>
            <Badge variant="warning">Pending Assets</Badge>
          </div>

          {unresolvedRequired.length > 0 && (
            <p className="mt-3 text-xs text-yellow-600 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {unresolvedRequired.length} required slot{unresolvedRequired.length === 1 ? '' : 's'} still missing
            </p>
          )}
        </Card>

        {/* Slots needing attention */}
        {needsAttention.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-text-primary">Slots requiring action</h3>
            {needsAttention.map((slotId) => {
              const slot = templateSlots.find((s) => s.slotId === slotId);
              if (!slot || slot.visible === false) return null;
              return (
                <SlotRow
                  key={slotId}
                  slot={slot}
                  existingProp={job.props?.[slotId]}
                  fillState={fillStates.find((f) => f.slotId === slotId)}
                  isMissing={job.missing_required_slots.includes(slotId)}
                  isUnmatched={job.unmatched_slots.includes(slotId)}
                  onFileAdded={handleFileAdded}
                  onFileRemoved={handleFileRemoved}
                  disabled={submitting || uploading}
                />
              );
            })}
          </div>
        )}

        {/* Pipeline-filled slots (read-only overview) */}
        {templateSlots.filter((s) => s.visible !== false && !needsAttention.includes(s.slotId)).length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-text-primary">Pipeline-filled slots</h3>
            {templateSlots
              .filter((s) => s.visible !== false && !needsAttention.includes(s.slotId))
              .map((slot) => (
                <SlotRow
                  key={slot.slotId}
                  slot={slot}
                  existingProp={job.props?.[slot.slotId]}
                  fillState={undefined}
                  isMissing={false}
                  isUnmatched={false}
                  onFileAdded={() => {}}
                  onFileRemoved={() => {}}
                  disabled={true}
                />
              ))}
          </div>
        )}

        {/* Submit error */}
        {submitError && (
          <p className="text-xs text-accent-red">{submitError}</p>
        )}

        {/* Render button */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-text-tertiary">
            {canSubmit
              ? 'All required slots are filled. Ready to render.'
              : `${unresolvedRequired.length} required slot${unresolvedRequired.length === 1 ? '' : 's'} still missing.`}
          </p>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isLoading={submitting || uploading}
          >
            {submitting || uploading ? 'Submitting…' : 'Render'}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}

export default PipelineResolvePage;
